import React, { useState, useEffect } from 'react';
import WidgetWrapper from './WidgetWrapper';

const fmtBytes = (b) => {
  if (!b || b === 0) return '';
  const GB = 1024 * 1024 * 1024;
  return b >= GB ? (b / GB).toFixed(1) + ' GB' : Math.round(b / (1024 * 1024)) + ' MB';
};

const BackupWidget = ({ darkMode = false, onError }) => {
  const [backupData, setBackupData] = useState(null);
  const [axcientData, setAxcientData] = useState(null);
  const [axcientLoading, setAxcientLoading] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [backupDate, setBackupDate] = useState(new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        try {
          const backupRes = await fetch('/api/veeam?date=' + backupDate);
          const backup = await backupRes.json();
          setBackupData(backup);
        } catch {
          setBackupData({ jobs: [], summary: { total: 0, success: 0, failed: 0, running: 0 } });
        }

        try {
          setAxcientLoading(true);
          const axcientRes = await fetch('/api/axcient');
          const axcient = await axcientRes.json();
          setAxcientData(axcient);
        } catch {
          setAxcientData(null);
        } finally {
          setAxcientLoading(false);
        }
      } catch (error) {
        console.error('BackupWidget error:', error);
        onError?.('BackupWidget', error.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [backupDate, onError]);

  const subTextColor = darkMode ? '#9ca3af' : '#6b7280';
  const textColor = darkMode ? '#f3f4f6' : '#111827';
  const cardBg = darkMode ? '#1f2937' : '#f9fafb';
  const borderColor = darkMode ? '#374151' : '#e5e7eb';
  const axDevices = axcientData?.devices || [];
  const normalCount = axDevices.filter(d => d.healthStatus === 'NORMAL').length;
  const warnCount = axDevices.filter(d => d.healthStatus !== 'NORMAL').length;
  const totalCloudSize = axDevices.reduce((sum, d) => sum + (d.cloudUsage || d.cloud_usage || 0), 0);

  const handleDateChange = (delta) => {
    const d = new Date(backupDate);
    d.setDate(d.getDate() + delta);
    setBackupDate(d.toISOString().split('T')[0]);
  };

  const statusColor = (s) => {
    if (!s) return subTextColor;
    if (s === 'NORMAL' || s === 'Success' || s === 'Completed') return '#22c55e';
    if (s === 'Running' || s === 'IN_PROGRESS') return '#3b82f6';
    return '#ef4444';
  };

  const statusIcon = (s) => {
    if (!s) return '✗';
    if (s === 'NORMAL' || s === 'Success' || s === 'Completed') return '✓';
    if (s === 'Running' || s === 'IN_PROGRESS') return '⟳';
    return '✗';
  };

  return (
    <WidgetWrapper title="💾 Backups" loading={loading} darkMode={darkMode}>
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>

        {/* NexReGenX (Veeam) Section */}
        <div style={{ flex: '1 1 200px', minWidth: 200 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <span style={{ fontWeight: 700, color: '#3b82f6', fontSize: 13 }}>NexReGenX</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <button onClick={() => handleDateChange(-1)} style={{ background: 'none', border: 'none', color: subTextColor, cursor: 'pointer', fontSize: 14, padding: '2px 6px' }}>«</button>
              <span style={{ fontSize: 11, color: subTextColor, fontFamily: 'monospace' }}>{backupDate}</span>
              <button onClick={() => handleDateChange(1)} style={{ background: 'none', border: 'none', color: subTextColor, cursor: 'pointer', fontSize: 14, padding: '2px 6px' }}>»</button>
            </div>
          </div>
          
          {!backupData || !backupData.jobs || backupData.jobs.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 20, background: cardBg, borderRadius: 8, border: `1px solid ${borderColor}` }}>
              <span style={{ fontSize: 24, display: 'block', marginBottom: 8 }}>📦</span>
              <span style={{ fontSize: 12, color: subTextColor }}>No backup data available</span>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {/* Summary row */}
              <div style={{ display: 'flex', gap: 12, marginBottom: 4 }}>
                <span style={{ background: '#22c55e22', color: '#22c55e', padding: '2px 8px', borderRadius: 4, fontSize: 11 }}>
                  ✓ {backupData.summary?.success || 0} success
                </span>
                {backupData.summary?.failed > 0 && (
                  <span style={{ background: '#ef444422', color: '#ef4444', padding: '2px 8px', borderRadius: 4, fontSize: 11 }}>
                    ✗ {backupData.summary?.failed} failed
                  </span>
                )}
              </div>
              
              {/* Backup job cards */}
              {(showAll ? backupData.jobs : backupData.jobs.slice(0, 5)).map((job, idx) => (
                <div key={idx} style={{ 
                  background: cardBg, 
                  border: `1px solid ${borderColor}`, 
                  borderRadius: 6, 
                  padding: '8px 10px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <div style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    <div style={{ fontSize: 12, fontWeight: 500, color: textColor }}>
                      {job.name || `Job ${idx + 1}`}
                    </div>
                    {job.size && <div style={{ fontSize: 10, color: subTextColor }}>{job.size}</div>}
                  </div>
                  <span style={{ 
                    fontSize: 11, 
                    fontWeight: 600,
                    color: statusColor(job.status),
                    background: statusColor(job.status) + '22',
                    padding: '2px 8px',
                    borderRadius: 4
                  }}>
                    {statusIcon(job.status)} {job.status || 'Unknown'}
                  </span>
                </div>
              ))}
              
              {backupData.jobs.length > 5 && (
                <button
                  onClick={() => setShowAll(!showAll)}
                  style={{ background: 'none', border: `1px solid ${borderColor}`, color: '#3b82f6', cursor: 'pointer', fontSize: 10, marginTop: 4, padding: '4px 8px', borderRadius: 4, width: '100%' }}
                >
                  {showAll ? '▲ Show less' : `▼ Show all (${backupData.jobs.length})`}
                </button>
              )}
            </div>
          )}
        </div>

        {/* Axcient Section */}
        <div style={{ flex: '1 1 200px', minWidth: 200 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <span style={{ fontWeight: 700, color: '#8b5cf6', fontSize: 13 }}>Axcient</span>
            {totalCloudSize > 0 && (
              <span style={{ fontSize: 10, color: subTextColor, background: cardBg, padding: '2px 6px', borderRadius: 4, border: `1px solid ${borderColor}` }}>
                ☁ {fmtBytes(totalCloudSize)}
              </span>
            )}
          </div>

          {axcientLoading ? (
            <div style={{ textAlign: 'center', padding: 20, background: cardBg, borderRadius: 8, border: `1px solid ${borderColor}` }}>
              <span style={{ color: subTextColor }}>Loading...</span>
            </div>
          ) : axcientData?.source === 'error' ? (
            <div style={{ textAlign: 'center', padding: 20, background: '#ef444411', borderRadius: 8, border: '1px solid #ef444444' }}>
              <span style={{ fontSize: 20, display: 'block', marginBottom: 4 }}>⚠️</span>
              <span style={{ fontSize: 11, color: '#ef4444' }}>API Error</span>
            </div>
          ) : axDevices.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 20, background: cardBg, borderRadius: 8, border: `1px solid ${borderColor}` }}>
              <span style={{ fontSize: 20, display: 'block', marginBottom: 4 }}>📦</span>
              <span style={{ fontSize: 11, color: subTextColor }}>No backups</span>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {/* Summary row */}
              <div style={{ display: 'flex', gap: 12, marginBottom: 4 }}>
                <span style={{ background: '#22c55e22', color: '#22c55e', padding: '2px 8px', borderRadius: 4, fontSize: 11 }}>
                  ✓ {normalCount} healthy
                </span>
                {warnCount > 0 && (
                  <span style={{ background: '#ef444422', color: '#ef4444', padding: '2px 8px', borderRadius: 4, fontSize: 11 }}>
                    ✗ {warnCount} issues
                  </span>
                )}
              </div>

              {/* Device cards */}
              {(showAll ? axDevices : axDevices.slice(0, 5)).map((dev, idx) => {
                const size = dev.cloudUsage || dev.cloud_usage || 0;
                const job = dev.jobs?.[0] || {};
                const status = dev.healthStatus || job.health_status || dev.status || 'NORMAL';
                return (
                  <div key={dev.id || idx} style={{ 
                    background: cardBg, 
                    border: `1px solid ${borderColor}`, 
                    borderRadius: 6, 
                    padding: '8px 10px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <div style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      <div style={{ fontSize: 12, fontWeight: 500, color: textColor }}>
                        {dev.name || `Device ${idx + 1}`}
                      </div>
                      <div style={{ fontSize: 10, color: subTextColor }}>
                        {dev.type || 'Workstation'} • {fmtBytes(size)}
                      </div>
                    </div>
                    <span style={{ 
                      fontSize: 11, 
                      fontWeight: 600,
                      color: statusColor(status),
                      background: statusColor(status) + '22',
                      padding: '2px 8px',
                      borderRadius: 4
                    }}>
                      {statusIcon(status)}
                    </span>
                  </div>
                );
              })}
              
              {axDevices.length > 5 && (
                <button
                  onClick={() => setShowAll(!showAll)}
                  style={{ background: 'none', border: `1px solid ${borderColor}`, color: '#8b5cf6', cursor: 'pointer', fontSize: 10, marginTop: 4, padding: '4px 8px', borderRadius: 4, width: '100%' }}
                >
                  {showAll ? '▲ Show less' : `▼ Show all (${axDevices.length})`}
                </button>
              )}
            </div>
          )}
        </div>

      </div>
    </WidgetWrapper>
  );
};

BackupWidget.widgetId = 'backup';
BackupWidget.title = '💾 Backups';
BackupWidget.defaultSize = { cols: 4 };
BackupWidget.category = 'infrastructure';

export default BackupWidget;